import { Box, Flex, Spinner, Text, VStack, useToast } from '@chakra-ui/react';
import axios from 'axios';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCancelWorkflow } from '../hooks/useCancelWorkflow';
import { useGetOrderWorkflowStatus, type AutomationTarget } from '../hooks/useGetOrderWorkflowStatus';
import { Button, Card, Icon, IconButton, Popover } from '../ui';
import SourceBadge from './SourceBadge';

interface OrderWorkflowOverlayProps {
    orderId: string;
}

interface CancellableWorkflow {
    key: string;
    label: string;
    target: AutomationTarget;
    canCancel?: boolean;
}

const cancelWorkflowToast = (status: number | undefined) => {
    if (status === 409) {
        return {
            title: 'This has already started',
            description: 'It can no longer be cancelled.',
            status: 'warning' as const,
        };
    }
    return {
        title: 'Unable to cancel',
        status: 'error' as const,
    };
};

const WorkflowCancelButton = ({
    orderId,
    workflow,
}: {
    orderId: string;
    workflow: CancellableWorkflow;
}) => {
    const toast = useToast();
    const cancelWorkflow = useCancelWorkflow();
    const [isOpen, setIsOpen] = useState(false);

    const handleConfirm = () => {
        cancelWorkflow.mutate(
            { orderId, workflowId: workflow.key },
            {
                onSuccess: () => {
                    setIsOpen(false);
                    toast({
                        title: 'Cancelled successfully',
                        status: 'success',
                    });
                },
                onError: (error) => {
                    const status = axios.isAxiosError(error) ? error.response?.status : undefined;
                    toast(cancelWorkflowToast(status));
                },
            },
        );
    };

    return (
        <Popover
            isOpen={isOpen}
            onOpen={() => setIsOpen(true)}
            onClose={() => setIsOpen(false)}
            placement="top-start"
            isLazy
            showArrow
            width="260px"
            trigger={
                <IconButton
                    aria-label="Cancel"
                    icon={<Icon name="x" size="sm" />}
                    size="xs"
                    variant="ghostGray"
                    isDisabled={cancelWorkflow.isPending}
                />
            }
        >
            <Box p={3}>
                <Text textStyle="body" color="fg.default">
                    Are you sure?
                </Text>
                <Text fontSize="xs" color="fg.muted" mt={2}>
                    There may be a few seconds of delay. If this has already started, the cancellation will have no effect.
                </Text>
                <Flex justify="flex-end" mt={3}>
                    <Button
                        size="sm"
                        colorScheme="red"
                        onClick={handleConfirm}
                        isLoading={cancelWorkflow.isPending}
                    >
                        Cancel
                    </Button>
                </Flex>
            </Box>
        </Popover>
    );
};

const OrderWorkflowOverlay = ({ orderId }: OrderWorkflowOverlayProps) => {
    const queryClient = useQueryClient();
    const hadWorkflowRef = useRef(false);
    const [refetchInterval, setRefetchInterval] = useState<number>(10 * 1000);
    const { data: workflowStatus } = useGetOrderWorkflowStatus(orderId, refetchInterval);
    const workflowItems = useMemo(
        () => [...(workflowStatus?.inProgress ?? []), ...(workflowStatus?.pending ?? [])],
        [workflowStatus],
    );

    useEffect(() => {
        setRefetchInterval(workflowItems.length > 0 ? 3 * 1000 : 10 * 1000);
    }, [workflowItems.length]);

    useEffect(() => {
        const hasWorkflow = workflowItems.length > 0;
        if (hadWorkflowRef.current && !hasWorkflow && orderId) {
            queryClient.refetchQueries({ queryKey: ['/order/detail', orderId], exact: true });
        }
        hadWorkflowRef.current = hasWorkflow;
    }, [workflowItems.length, orderId, queryClient]);

    if (workflowItems.length === 0) return null;

    return (
        <VStack
            position="fixed"
            left="50%"
            bottom={8}
            transform="translateX(-50%)"
            zIndex={30}
            spacing={3}
            align="stretch"
        >
            {workflowItems.map((workflowItem) => (
                <Card
                    key={`${workflowItem.key}-${workflowItem.target}`}
                    variant="outline"
                    minW="320px"
                    maxW="400px"
                >
                    <Flex gap={3} align="center">
                        {workflowItem.canCancel && (
                            <WorkflowCancelButton orderId={orderId} workflow={workflowItem} />
                        )}
                        <SourceBadge source={workflowItem.target} />
                        <Text fontWeight="semibold" textStyle="body" color="fg.default" flex={1} minW={0}>
                            {workflowItem.label}
                        </Text>
                        <Spinner size="sm" color="brand.solid" thickness="2px" />
                    </Flex>
                </Card>
            ))}
        </VStack>
    );
};

export default OrderWorkflowOverlay;
